import { QueryClient } from '@tanstack/react-query';
import { qkAttributeValues, qkAttributeGroups } from '@/lib/queryKeys';
import { Attribute } from '@/types/attribute';

/**
 * Smart cache invalidation for attribute values based on attribute properties
 *
 * For non-localized/scopable attributes:
 * - Invalidates ALL locale/channel combinations since the change should be visible everywhere
 *
 * For localized/scopable attributes:
 * - Only invalidates the current locale/channel combination
 *
 * @param queryClient - React Query client instance
 * @param attribute - The attribute definition with localization properties
 * @param productId - Product ID
 * @param currentLocale - Currently selected locale
 * @param currentChannel - Currently selected channel
 * @param enableGroups - Whether attribute groups are enabled
 */
export function invalidateAttributeValueCache(
  queryClient: QueryClient,
  attribute: Attribute,
  productId: number,
  currentLocale: string,
  currentChannel: string,
  enableGroups: boolean
): void {
  // Determine if this attribute requires broad invalidation
  // An attribute is global (non-localized/scopable) if both properties are explicitly false
  // or if they're undefined (default behavior for older attributes)
  const isGlobalAttribute = !attribute.is_localisable && !attribute.is_scopable;

  if (isGlobalAttribute) {
    // For non-localized/scopable attributes, invalidate ALL locale/channel combinations
    // Use pattern matching to invalidate all variations
    const baseKey = enableGroups ? 'attrGroups' : 'attrValues';

    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        return Array.isArray(key) && key.length >= 2 && key[0] === baseKey && key[1] === productId;
      },
    });
  } else {
    // For localized/scopable attributes, only invalidate current combination
    const queryKey = enableGroups
      ? qkAttributeGroups(productId, currentLocale, currentChannel)
      : qkAttributeValues(productId, currentLocale, currentChannel);

    queryClient.invalidateQueries({ queryKey });
  }

  // Always invalidate completeness-related queries since they aggregate across all locales/channels
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'incomplete-products'] });
}

/**
 * Helper function to get attribute definition by ID from a list of attributes
 *
 * @param attributes - List of available attributes
 * @param attributeId - ID of the attribute to find
 * @returns Attribute definition or null if not found
 */
export function getAttributeById(attributes: Attribute[], attributeId: number): Attribute | null {
  return attributes.find(attr => attr.id === attributeId) || null;
}

/**
 * Smart cache invalidation wrapper that handles attribute lookup
 *
 * @param queryClient - React Query client instance
 * @param attributes - List of available attributes
 * @param attributeId - ID of the attribute being updated
 * @param productId - Product ID
 * @param currentLocale - Currently selected locale
 * @param currentChannel - Currently selected channel
 * @param enableGroups - Whether attribute groups are enabled
 */
export function invalidateAttributeValueCacheById(
  queryClient: QueryClient,
  attributes: Attribute[],
  attributeId: number,
  productId: number,
  currentLocale: string,
  currentChannel: string,
  enableGroups: boolean
): void {
  const attribute = getAttributeById(attributes, attributeId);

  if (!attribute) {
    // Fallback to current locale/channel only if attribute not found
    const queryKey = enableGroups
      ? qkAttributeGroups(productId, currentLocale, currentChannel)
      : qkAttributeValues(productId, currentLocale, currentChannel);

    queryClient.invalidateQueries({ queryKey });
    return;
  }

  invalidateAttributeValueCache(
    queryClient,
    attribute,
    productId,
    currentLocale,
    currentChannel,
    enableGroups
  );
}
